/** @jsx jsx */

import { debounce } from 'lodash';
import { useState } from 'react';

import { Alert, Dropdown, Margin, Paginator } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import LoadingBlock from 'app/common/components/LoadingBlock';
import { LayoutStyles } from 'app/developer-v3/layouts/constants';
import { useQueryIssues, useRouter } from 'app/developer-v3/hooks';
import { getAppUrl } from 'app/developer-v3/utils';

import IssueItem from './IssueItem';
import IssueOrder from './IssueOrder';
import NoIssues from './NoIssues';
import TriggersActionsDropdown from './TriggersActionsDropdown';
import {
  ISSUE_TYPE_ITEMS,
  IssueOrderBy,
  IssueOrderDirection,
} from '../constants';
import {
  getFilters,
  reverseIssueOrderDirection,
  updateSearchParamsByFilters,
} from '../utils';

import type { SearchActionSchema } from 'app/developer-v3/platformSchema/search-action';
import type { CreateActionSchema } from 'app/developer-v3/platformSchema/create-action';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

const Styles = {
  main: css`
    max-width: none;
    padding: ${LayoutStyles.verticalPadding} ${LayoutStyles.horizontalPadding};
  `,
  orderWrapper: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap-reverse;
    gap: 10px;
    margin-bottom: 15px;
  `,
  paginator: css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px;
  `,
  filtersWrapper: css`
    flex: 1;
    display: flex;
    gap: 8px;
  `,
  rightFilters: css`
    display: flex;
    align-items: flex-end;
    gap: 10px;
  `,
  typeFilter: css`
    display: flex;
    flex-direction: column;
    width: 175px;

    & > span {
      margin-bottom: 8px;
      font-weight: 600;
    }
  `,
};

type Props = {
  actions: Record<string, CreateActionSchema | SearchActionSchema>;
  triggers: Record<string, TriggerSchema>;
};

function IssueList({ actions, triggers }: Props) {
  const { params, query, routeTo } = useRouter();
  const integrationId = Number(params.id);

  const {
    actionKeys,
    direction,
    issueTypeItem,
    orderBy,
    page,
    triggerKeys,
  } = getFilters(query);

  const [triggerKeysCached, setTriggerKeysCached] = useState(triggerKeys);
  const [actionKeysCached, setActionKeysCached] = useState(actionKeys);

  // Convert issue type filter value to API format
  const typeOf =
    issueTypeItem.value === 'Bug'
      ? 'bug'
      : issueTypeItem.value === 'Feature Request'
      ? 'feature-request'
      : undefined;

  const { isLoading, data } = useQueryIssues(
    {
      actions: [...triggerKeys, ...actionKeys],
      integrationId,
      orderBy,
      orderDirection:
        // Every issue has priority and priority_rank numbers. The lower the
        // priority number is, the higher the priority_rank number is. When we
        // sort the issues by priority in the UI, we really want to sort them by
        // priority_rank in the opposite order.
        //
        // Ex: Issues in descending order of priority in the UI is the most critical
        // issue at the top. The most critical issue has the highest
        // priority_rank number and the lowest priority number. The descending
        // order of priority is actually ascending order of priority_rank.
        orderBy === IssueOrderBy.PRIORITY
          ? reverseIssueOrderDirection(direction)
          : direction,
      page: page + 1, // The endpoint is 1-based paginated.
      typeOf,
    },
    { keepPreviousData: true, staleTime: 60000 }
  );

  const { issues = [], pages = [], totalPages = 0 } = data ?? {};

  const issuesRoute = `${getAppUrl(integrationId)}/issues`;
  const searchParams = new URLSearchParams(query);

  const onUpdateFilters = (
    filter:
      | { actionKeys: string[] }
      | { direction: 'asc' | 'desc' }
      | { issueType: 'all' | 'bug' | 'feature-request' }
      | { orderBy: 'created-at' | 'priority' }
      | { page: string }
      | { triggerKeys: string[] }
  ) => {
    const searchParamsNext = updateSearchParamsByFilters(searchParams, filter);
    const pathNext = `${issuesRoute}?${searchParamsNext.toString()}`;
    if ('actionKeys' in filter || 'triggerKeys' in filter) {
      debounce(routeTo, 1000)(pathNext);
    } else {
      routeTo(pathNext);
    }
  };

  const onChangeTriggerActionItems = (
    selectedItemsNext: { label: string; value: string }[],
    keyName: 'actionKeys' | 'triggerKeys'
  ) => {
    const selectedKeys = selectedItemsNext.map(({ value }) => value);

    // This is a techique to make the selectors responsive.
    // The selectors update right away but the query param updates are
    // batched. This way we don't query too frequently.
    if (keyName === 'actionKeys') {
      setActionKeysCached(selectedKeys);
      onUpdateFilters({ actionKeys: selectedKeys });
    } else {
      setTriggerKeysCached(selectedKeys);
      onUpdateFilters({ triggerKeys: selectedKeys });
    }
  };

  return isLoading ? (
    <LoadingBlock />
  ) : (
    <main css={Styles.main}>
      <Margin margin="0px 0px 15px">
        <Alert status="info">
          Not all bugs and feature requests have been tagged with the related
          trigger or action. Only the more recent ones have.
        </Alert>
      </Margin>
      <div css={Styles.orderWrapper}>
        <div css={Styles.filtersWrapper}>
          <TriggersActionsDropdown
            items={Object.values(triggers).map(trigger => ({
              value: trigger.key,
              label: trigger.display?.label,
            }))}
            label="Trigger"
            onChange={selectedItemsNext =>
              onChangeTriggerActionItems(selectedItemsNext, 'triggerKeys')
            }
            selectedItems={triggerKeysCached.map(triggerKey => ({
              label: triggers[triggerKey]?.display?.label,
              value: triggerKey,
            }))}
            type="triggers"
          />
          <TriggersActionsDropdown
            items={Object.values(actions).map(action => ({
              value: action.key,
              label: action.display?.label,
            }))}
            label="Action"
            onChange={selectedItemsNext =>
              onChangeTriggerActionItems(selectedItemsNext, 'actionKeys')
            }
            selectedItems={actionKeysCached.map(actionKey => ({
              label: actions[actionKey]?.display?.label,
              value: actionKey,
            }))}
            type="actions"
          />
        </div>
        <div css={Styles.rightFilters}>
          <div css={Styles.typeFilter}>
            <span>Type</span>
            <Dropdown
              items={ISSUE_TYPE_ITEMS}
              menuAriaLabel="Filter issue type"
              onChange={filterNext => {
                onUpdateFilters({
                  issueType:
                    filterNext.value === ISSUE_TYPE_ITEMS[0].value
                      ? 'all'
                      : filterNext.value === ISSUE_TYPE_ITEMS[1].value
                      ? 'bug'
                      : 'feature-request',
                });
              }}
              placeholder="Type"
              selectedItem={issueTypeItem}
            />
          </div>
          <IssueOrder
            onChangeOrderBy={orderByNext => {
              searchParams.set(
                'order-by',
                String(
                  orderByNext === IssueOrderBy.CREATED_AT
                    ? 'created-at'
                    : 'priority'
                )
              );
              routeTo(`${issuesRoute}?${searchParams.toString()}`);
            }}
            onChangeOrderDirection={orderDirectionNext => {
              onUpdateFilters({
                direction:
                  orderDirectionNext === IssueOrderDirection.ASC
                    ? 'asc'
                    : 'desc',
              });
            }}
            orderBy={orderBy}
            orderDirection={direction}
          />
        </div>
      </div>
      {issues.length === 0 ? (
        <NoIssues issueType={issueTypeItem.value} />
      ) : (
        issues.map(issue => (
          <IssueItem
            issue={issue}
            key={issue.absoluteId}
            route={`${issuesRoute}/${issue.id}`}
          />
        ))
      )}
      {pages.length !== 0 && (
        <div css={Styles.paginator}>
          <Paginator
            currentPageIndex={page}
            onPaginate={(paginatingData, event) => {
              event.preventDefault();
              onUpdateFilters({ page: String(paginatingData.pageIndex) });
            }}
            pageUrls={Array.from({ length: totalPages }).map((_, pageIndex) => {
              const searchParamsTmp = new URLSearchParams(searchParams);
              searchParamsTmp.set('page', String(pageIndex + 1));
              return `${issuesRoute}?${searchParamsTmp.toString()}`;
            })}
          />
        </div>
      )}
    </main>
  );
}

export default IssueList;
